import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './BlockedPayoutsPage.css';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || 'https://api.beetlestore.online').replace(/\/$/, '');
const API_KEY     = process.env.REACT_APP_BACKEND_API_KEY || '';
const HEADERS     = { 'Content-Type': 'application/json', 'X-API-Key': API_KEY };

async function apiFetch(path, opts = {}) {
  const res  = await fetch(`${BACKEND_URL}${path}`, { headers: HEADERS, ...opts });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
  return data;
}

const fmtTZS = (n) => {
  if (!n && n !== 0) return '—';
  const num = Number(n);
  if (num >= 1_000_000) return `TZS ${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000)     return `TZS ${(num / 1_000).toFixed(0)}K`;
  return `TZS ${num.toLocaleString()}`;
};

const BlockedPayoutsPage = () => {
  const { t } = useTranslation();
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [acting, setActing]   = useState({});
  const [toast, setToast]     = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await apiFetch('/api/admin/payouts/blocked');
      setSellers(res.sellers || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const clear = async (sellerId) => {
    setActing((a) => ({ ...a, [sellerId]: true }));
    try {
      await apiFetch(`/api/admin/payouts/blocked/${sellerId}/clear`, { method: 'POST' });
      setSellers((prev) => prev.filter((s) => s.seller_id !== sellerId));
      showToast(t('blockedPayouts.resumedToast'));
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setActing((a) => ({ ...a, [sellerId]: false }));
    }
  };

  const total = sellers.reduce((s, r) => s + Number(r.available || 0), 0);

  return (
    <div className="bp-page">
      {toast && <div className={`bp-toast bp-toast--${toast.type}`}>{toast.msg}</div>}

      <div className="bp-header">
        <div>
          <h1 className="dashboard-title">{t('nav.blockedPayouts')}</h1>
          <p className="dashboard-subtitle">{t('blockedPayouts.subtitle')}</p>
        </div>
        <button className="bp-refresh" onClick={load} disabled={loading}>
          {loading ? '…' : `↻ ${t('common.refresh')}`}
        </button>
      </div>

      {error && (
        <div className="bp-error">{error} <button onClick={load}>{t('common.retry')}</button></div>
      )}

      {!loading && !error && (
        <p className="bp-summary">
          {t('blockedPayouts.summary', { count: sellers.length, amount: fmtTZS(total) })}
        </p>
      )}

      {!loading && !error && sellers.length === 0 && (
        <div className="bp-empty">{t('blockedPayouts.none')}</div>
      )}

      {!loading && sellers.length > 0 && (
        <div className="bp-table-wrap">
          <table className="bp-table">
            <thead>
              <tr>
                <th>{t('blockedPayouts.sellerId')}</th>
                <th>{t('blockedPayouts.available')}</th>
                <th>{t('blockedPayouts.failures')}</th>
                <th>{t('blockedPayouts.lastError')}</th>
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {sellers.map((s) => {
                const busy = acting[s.seller_id];
                return (
                  <tr key={s.seller_id}>
                    <td><code className="bp-code">{s.seller_id}</code></td>
                    <td className="bp-amount">{fmtTZS(s.available)}</td>
                    <td>{s.failures}</td>
                    <td className="bp-note-cell">{s.last_error || '—'}</td>
                    <td>
                      <button
                        className="bp-btn bp-btn--success bp-btn--sm"
                        onClick={() => clear(s.seller_id)}
                        disabled={busy}
                      >
                        {busy ? '…' : t('blockedPayouts.resumeRetrying')}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {loading && (
        <div className="bp-loading"><div className="mp-spinner" /> {t('common.loading')}</div>
      )}
    </div>
  );
};

export default BlockedPayoutsPage;
